import { type MenuOption } from '@opallabs/concepts'
import { Size } from '@opallabs/gem'
import { Icon, SingleSelect } from '@opallabs/lapidary'
import { type CalendarSortBy } from '../../MonthCalendarGrid/types'
import * as styles from './styles'

/** What a month can be sorted by, in the order the control offers them. */
const SORTS: ReadonlyArray<{ label: string; value: CalendarSortBy }> = [
  { label: 'None', value: 'none' },
  { label: 'Account', value: 'account' },
  { label: 'Channel', value: 'channel' },
  { label: 'Label', value: 'label' },
  { label: 'Moment', value: 'moment' },
]

export type SortControlProps = Readonly<{
  onChange: (sortBy: CalendarSortBy) => void
  sortBy: CalendarSortBy
}>

/**
 * What each day's content is put in order by.
 *
 * It sits beside the filters, where the week view puts the same control, so a
 * person moving between the two timescales finds it in the same place. It is
 * the select the view sheet's Titles rows use, drawn as a toolbar button: the
 * chosen answer wears a mark, and the others wear nothing.
 */
export const SortControl: React.FC<SortControlProps> = ({
  onChange,
  sortBy,
}) => {
  const options: Array<MenuOption<CalendarSortBy>> = SORTS.map((sort) => ({
    label: sort.label,
    trailingNode: <Icon ariaHidden={true} glyph="gem-check-circle" />,
    value: sort.value,
  }))
  const chosen = options.find((option) => option.value === sortBy) ?? null
  const isSorted = sortBy !== 'none' && chosen !== null

  return (
    <span css={styles.sortControl}>
      <SingleSelect.ControlledRoot
        options={options}
        selectedOption={chosen}
        onChange={(option) => option && onChange(option.value)}
      >
        {/* A sort is seen on the grid at once, so the menu stays open for a
            person to try the next one; the button, Escape or a click away
            closes it. */}
        <SingleSelect.Body shouldStayOpenOnSelect={true}>
          <SingleSelect.Trigger
            // The button says only what it is set to, so its name has to say
            // what that setting is.
            ariaLabel={isSorted ? `Group, ${chosen.label}` : 'Group'}
            hideTrailingNode={true}
            inputWidth="auto"
          >
            <Icon ariaHidden={true} glyph="gem-rows-stacked" size={Size.Icon} />
            {isSorted ? chosen.label : 'Group'}
          </SingleSelect.Trigger>
          <SingleSelect.Menu portalled={true}>
            <SingleSelect.MenuItems hideTrailingNodeUnlessSelected={true} />
          </SingleSelect.Menu>
        </SingleSelect.Body>
      </SingleSelect.ControlledRoot>
    </span>
  )
}
SortControl.displayName = 'Calendar: SortControl'
